import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';

import { FHAutocomplete, FHInputField } from '../../../../../../../../../base';
import { useTreatmentPlanEditable } from '../../../../../../../hooks/useTreatmentPlanEditable';
import { assessmentActions } from '../../../../../../../../../../store';
import { Themes } from '../../../../../../../../../../constants';
import { assessmentBarriersSchema } from '../../../../../../utils/validationSchema';
import { debounce, validateSchema, validateSync } from '../../../../../../../../../../helpers';
import { ERROR_STATES, STATE_IDS } from '../../../../../../constants/states';

import type { ChangeEvent } from 'react';
import type { IOption } from '../../../../../../../../../../utils/types';

interface BarrierFieldsProps {
  barrier?: IOption;
  filteredBarrierOptions: IOption[];
}

function BarrierFields({ barrier, filteredBarrierOptions }: BarrierFieldsProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const editable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { assessment, barriersLoading, updatingBarrier } = useSelector(
    (state: any) => state.assessments,
  );

  const [showDescription, setShowDescription] = useState<boolean>(false);

  const [barrierInfo, setBarrierInfo] = useState<IOption>({
    label: '',
  });
  const [errors, setErrors] = useState<any>({});

  const planId = treatmentPlan.id;
  const assessmentId = assessment.id;

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [`${ERROR_STATES[STATE_IDS.ASSESSMENT_BARRIER]}-${barrier?.id ? barrier?.id : 'new'}`]:
            errorCount,
        },
      }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);

  useEffect(() => {
    debouncedErrorUpdater(errors);
    // eslint-disable
  }, [errors]);

  useEffect(() => {
    if (!barrier) return;
    const { description, label } = barrier;

    if (description && label === 'Other') {
      setShowDescription(true);
    } else {
      setShowDescription(false);
    }
    setBarrierInfo(barrier);
  }, [barrier]);

  const handleFieldUpdate = (field: string, value: string) => {
    if (field === 'label') {
      if (value === 'Other') {
        setShowDescription(true);
      } else {
        setShowDescription(false);
      }
    }
    const updatedData = { ...barrierInfo, [field]: value };
    setBarrierInfo(updatedData);
    handleValidate(field, { ...barrierInfo, [field]: value });
  };

  const handleValidate = (key: string, data: any = barrierInfo) => {
    const isValid = validateSchema(assessmentBarriersSchema, key, data);
    setErrors((prev: any) => ({ ...prev, [key]: !isValid }));
  };

  const handleCreateOrUpdateBarrier = (labelVal: string) => {
    const data = {
      label: labelVal,
      description: labelVal === 'Other' ? barrierInfo.description : null,
    };

    const isValid = validateSync(assessmentBarriersSchema, data);
    if (!isValid) return;

    if (barrier?.id) {
      dispatch(
        assessmentActions.updateBarrier({
          data,
          getAccessTokenSilently,
          planId,
          assessmentId,
          barrierId: barrier.id,
        }),
      );
    } else {
      dispatch(
        assessmentActions.createBarrier({
          data,
          getAccessTokenSilently,
          planId,
          assessmentId,
        }),
      );
    }
  };

  return (
    <Box>
      <FHAutocomplete
        sx={{ minWidth: 240 }}
        value={barrierInfo?.label}
        options={filteredBarrierOptions}
        onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: any) => {
          handleFieldUpdate('label', newValue?.label);
          handleCreateOrUpdateBarrier(newValue?.label);
        }}
        isOptionEqualToValue={() => true}
        onFocus={() => handleValidate('label')}
        disabled={!editable || barriersLoading || updatingBarrier}
        colorTheme={Themes.Primary}
        inputHeight={40}
        error={errors['label']}
      />
      {showDescription && (
        <Box sx={{ display: 'flex', mt: 2 }}>
          <Typography fontWeight={'bold'} mr={1} mt={1}>
            If “other” add description:
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <FHInputField
              value={barrierInfo.description}
              sx={{ marginBottom: '1em' }}
              fullWidth
              disabled={!editable || barriersLoading}
              inputHeight={40}
              colorTheme={Themes.Primary}
              onChange={(e) => handleFieldUpdate('description', e.target.value)}
              onBlur={() => handleCreateOrUpdateBarrier(barrierInfo?.label)}
              onFocus={() => handleValidate('description')}
              error={errors['description']}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default BarrierFields;
